import {
  Country,
  ErasmusType,
  TravelActivityType,
  TravelDayBalanceSummary,
  TravelFundingSource,
  TravelRegistryType,
  TravelRequestStatus,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { Id, NamedEntity, PagedParams, PagedResponse } from '@feature/utils';

export interface TravelRegistryRow {
  id: number;
  registrationNumber: number;
  registrationYear: number;
  registrationDate: string;
  registryType: TravelRegistryType;
  requester: NamedEntity;
  jobTitle: string | null;
  facultyName: string | null;
  departmentName: string | null;
  disciplineName: string | null;
  activityType: TravelActivityType;
  erasmusType: ErasmusType | null;
  name: string | null;
  destinationInstitution: string | null;
  city: string | null;
  country: Country | null;
  departureDate: string | null;
  returnDate: string | null;
  // Server-computed calendar days, both ends included; the same holds for eventDays.
  travelDays: number | null;
  eventStartDate: string | null;
  eventEndDate: string | null;
  eventDays: number | null;
  requestDate: string | null;
  presentsPaper: boolean | null;
  paperTitle: string | null;
  status: TravelRequestStatus;
  lateSubmission: boolean;
  overlapCount: number;
  leaveDaysExceeded: boolean;
  dayCapExceeded: boolean;
  dayBalance: TravelDayBalanceSummary | null;
}

export interface TravelRegistryFilter {
  registrationYear: number | null;
  registryType: TravelRegistryType | null;
  statuses: TravelRequestStatus[];
  activityTypes: TravelActivityType[];
  facultyId: Id | null;
  departureFrom: string | null;
  departureTo: string | null;
}

export type TravelRegistryParams = PagedParams<TravelRegistryFilter>;

export type TravelRegistryPage = PagedResponse<TravelRegistryRow>;

export const travelRegistryLangList = ['ro', 'en'] as const;
export type TravelRegistryLang = (typeof travelRegistryLangList)[number];
export const DEFAULT_TRAVEL_REGISTRY_LANG: TravelRegistryLang = 'ro';

export interface TravelRegistryExportRequest extends TravelRegistryFilter {
  search: string;
  lang: TravelRegistryLang;
  // Column count, not row count: the filters still decide which rows are exported.
  full: boolean;
}

export interface TravelReportingParams {
  registrationYear: number | null;
  facultyId: Id | null;
}

export interface TravelYearlyAggregatesParams {
  from: number;
  to: number;
  facultyId: Id | null;
}

export interface TravelStaffSummaryExportRequest extends TravelReportingParams {
  lang: TravelRegistryLang;
}

export const WORLD_TOPOLOGY_URL = '/countries-110m.json';

// Only the shape the choropleth reads; the rest of the topojson is handed to react-simple-maps untouched.
export interface WorldTopology {
  objects: { countries: { geometries: { id?: string | number }[] } };
}

export interface TravelFundingAmount {
  fundingSource: TravelFundingSource;
  amountEur: number;
}

export interface TravelCountryAggregate {
  country: string;
  countryCode: string | null;
  trips: number;
  amountEur: number;
}

export interface TravelErasmusTypeAggregate {
  erasmusType: ErasmusType;
  trips: number;
  requesters: number;
}

export interface TravelErasmusAggregate {
  trips: number;
  requesters: number;
  byType: TravelErasmusTypeAggregate[];
  // Null for a faculty outside the caller's scope, where the amount is withheld rather than zero.
  amountEur: number | null;
}

export interface TravelAggregates {
  registrationYear: number;
  professorsTraveled: number;
  trips: number;
  financedDays: number;
  // Null only when the scope holds no travel at all; an empty-but-present scope reports 0.
  universityFinancedEur: number | null;
  byFundingSource: TravelFundingAmount[];
  byCountry: TravelCountryAggregate[];
  erasmus: TravelErasmusAggregate;
}
